import { PostgrestClient, PostgrestError } from "@supabase/postgrest-js";
import type { Database as GenericDatabase } from "@sweco-se/reality-frontend";
import { QueryClient } from "@tanstack/react-query";
import type { MergeDeep } from "type-fest";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (_, error) => {
        if (error instanceof PostgrestError) {
          // Only retry for connection errors (code starts with "PGRST00").
          // For all other PostgREST errors, disable retry
          if (error.code && !error.code.startsWith("PGRST00")) {
            return false;
          }
        }

        return true;
      },
    },
  },
});

export type Database = MergeDeep<
  GenericDatabase,
  {
    api: {
      Tables: {
        project: {
          Row: {
            id: number;
            identifier: string;
            name: string;
            start: string;
            end: string;
            is_member: boolean;
            customer_domain?: string | null;
            customer_id?: number | null;
          };
          Insert: {
            id?: never;
            identifier: string;
            name: string;
            start: string;
            end: string;
            is_member?: never;
            customer_domain?: string | null;
            customer_id?: number | null;
          };
          Update: {
            id?: never;
            identifier?: never;
            name?: never;
            start?: string;
            end?: string;
            is_member?: never;
            customer_domain?: string | null;
            customer_id?: number | null;
          };
          Relationships: [
            {
              foreignKeyName: "project_customer_id_fkey";
              columns: ["customer_id"];
              isOneToOne: false;
              referencedRelation: "customer";
              referencedColumns: ["id"];
            },
          ];
        };
        project_member: {
          Row: {
            project_id: number;
            user: string;
          };
          Insert: {
            project_id: number;
            user: string;
          };
          Update: {};
          Relationships: [
            {
              foreignKeyName: "project_member_project_id_fkey";
              columns: ["project_id"];
              isOneToOne: false;
              referencedRelation: "project";
              referencedColumns: ["id"];
            },
          ];
        };
        project_dataset: {
          Row: {
            project_id: number;
            dataset_uri: string;
          };
          Insert: {
            project_id: number;
            dataset_uri: string;
          };
          Update: {};
          Relationships: [
            {
              foreignKeyName: "project_dataset_project_id_fkey";
              columns: ["project_id"];
              isOneToOne: false;
              referencedRelation: "project";
              referencedColumns: ["id"];
            },
          ];
        };
        customer: {
          Row: {
            id: number;
            name: string;
            organisation_number: string;
            is_geodatasamverkan_member: boolean;
          };
          Insert: {
            name: string;
            organisation_number: string;
            is_geodatasamverkan_member: boolean;
          };
          Update: {
            name?: string;
            organisation_number?: string;
            is_geodatasamverkan_member?: boolean;
          };
          Relationships: [];
        };
        session: {
          Row: {
            started_by: string;
            price: number;
          };
        };
        transaction: {
          Row: {
            source_information?: {
              id: string;
              reader: string;
              base_url: string;
            };
          };
        };
        product: {
          Row: {
            id: number;
            name: string;
            type: "property-search" | "extraction";
            price: number;
            price_type: "fixed" | "per-object" | "per-area";
            user_category: "internal" | "external";
            customer_category?: "geodatasamverkan" | "other" | null;
            customer_id?: number | null;
          };
          Insert: {};
          Update: {};
          Relationships: [
            {
              foreignKeyName: "product_customer_id_fkey";
              columns: ["customer_id"];
              isOneToOne: false;
              referencedRelation: "customer";
              referencedColumns: ["id"];
            },
          ];
        };
        product_dataset: {
          Row: {
            product_id: number;
            dataset_id: number;
          };
          Insert: {};
          Update: {};
          Relationships: [
            {
              foreignKeyName: "product_dataset_product_id_fkey";
              columns: ["product_id"];
              isOneToOne: false;
              referencedRelation: "product";
              referencedColumns: ["id"];
            },
            {
              foreignKeyName: "product_dataset_dataset_id_fkey";
              columns: ["dataset_id"];
              isOneToOne: false;
              referencedRelation: "dataset";
              referencedColumns: ["id"];
            },
          ];
        };
        session_product: {
          Row: {
            session_id: string;
            product_id: number;
          };
          Insert: {};
          Update: {};
          Relationships: [
            {
              foreignKeyName: "session_product_session_id_fkey";
              columns: ["session_id"];
              isOneToOne: false;
              referencedRelation: "session";
              referencedColumns: ["started_by"];
            },
            {
              foreignKeyName: "session_product_product_id_fkey";
              columns: ["product_id"];
              isOneToOne: false;
              referencedRelation: "product";
              referencedColumns: ["id"];
            },
          ];
        };
        news_post: {
          Row: {
            id: number;
            level: "info" | "warning";
            published: boolean;
            pinned: boolean;
            show_from: string | null;
            show_to: string | null;
            title: string;
            body: string;
            page_prefixes: Array<string> | null;
            created_at: string;
            updated_at: string;
            created_by: string;
          };
          Insert: {
            id?: never;
            level?: "info" | "warning";
            published?: boolean;
            pinned?: boolean;
            show_from?: string | null;
            show_to?: string | null;
            title: string;
            body: string;
            page_prefixes?: Array<string> | null;
            created_at?: never;
            updated_at?: never;
            created_by?: never;
          };
          Update: {
            id?: number;
            level?: "info" | "warning";
            published?: boolean;
            pinned?: boolean;
            show_from?: string | null;
            show_to?: string | null;
            title?: string;
            body?: string;
            page_prefixes?: Array<string> | null;
            created_at?: never;
            updated_at?: never;
            created_by?: never;
          };
          Relationships: [];
        };
        source: {
          Row: {
            reader: string;
            base_url: string;
            project_id: number | null;
            customer_id: number | null;
            has_cost: boolean;
            internal_geodatasamverkan: boolean;
            internal_other: boolean;
            external_geodatasamverkan: boolean;
            external_other: boolean;
          };
          Insert: {};
          Update: {};
          Relationships: [
            {
              foreignKeyName: "source_customer_id_fkey";
              columns: ["customer_id"];
              isOneToOne: false;
              referencedRelation: "customer";
              referencedColumns: ["id"];
            },
          ];
        };
        client: {
          Row: {
            id: number;
            name: string;
            owned_by: string | null;
            redirect_uris: Array<string>;
            client_datasets: Array<string>;
            permitted_datasets: Array<string>;
            project_id: number | null;
            client_dataset_project_id: number | null;
          };
          Insert: {
            id: number;
            name: string;
            owned_by?: string | null;
            redirect_uris?: Array<string>;
            client_datasets?: Array<string>;
            permitted_datasets?: Array<string>;
            project_id?: number | null;
            client_dataset_project_id?: number | null;
          };
          Update: {
            id?: number;
            name?: string;
            owned_by?: string | null;
            redirect_uris?: Array<string>;
            client_datasets?: Array<string>;
            permitted_datasets?: Array<string>;
            project_id?: number | null;
            client_dataset_project_id?: number | null;
          };
          Relationships: [
            {
              foreignKeyName: "client_project_id_fkey";
              columns: ["project_id"];
              isOneToOne: false;
              referencedRelation: "project";
              referencedColumns: ["id"];
            },
            {
              foreignKeyName: "client_client_dataset_project_id_fkey";
              columns: ["client_dataset_project_id"];
              isOneToOne: false;
              referencedRelation: "project";
              referencedColumns: ["id"];
            },
          ];
        };
      };
      Views: {
        download_project_details: {
          Row: {
            id: number;
            project_id: number;
            queued_by: string;
            queued_at: string;
            datasets: Array<string>;
            geometry: string; // as WKT
            area: number;
            format: string;
            status: "queued" | "in_progress" | "completed" | "failed";
            project_identifier: string;
            project_name: string;
          };
          Insert: {};
          Update: {};
          Relationships: [
            {
              foreignKeyName: "download_project_id_fkey";
              columns: ["project_id"];
              isOneToOne: false;
              referencedRelation: "project";
              referencedColumns: ["id"];
            },
          ];
        };
      };
      Functions: {
        create_session_with_products: {
          Args: {
            p_project_id: number;
            p_product_ids: Array<number>;
            p_top_level_objects?: Record<string, Array<string>> | null;
            p_invoice_marker?: string | null;
          };
          Returns: {
            id: string;
            project_id: number | null;
            invoice_marker: string | null;
            client_id: number;
            started_at: string;
            datasets: Array<string> | null;
            top_level_objects: Record<string, Array<string>> | null;
            started_by: string;
          };
        };
      };
      Enums: {
        download_status: "queued" | "in_progress" | "completed" | "failed";
        transaction_type: "service_access" | "permission_add" | "download";
        news_level: "info" | "warning";
      };
      CompositeTypes: {};
    };
  }
>;

export const BACKEND_URL =
  import.meta.env.VITE_BACKEND_URL || window.location.origin;

export const api = new PostgrestClient<Database>(BACKEND_URL + "/api", {
  schema: "api",
});
